import React, { useEffect, useRef, useState } from 'react';
import '../form/Form.css';
import './AddOnEditorPopup.css';
import api from '../wrapper/API';
import Popup from '../popup/Popup';
import ActionButton from '../input/ActionButton';
import CustomDropdown from '../input/CustomDropdown';
import CustomTogglebox from '../input/CustomTogglebox';
import TextblockVariantTabs from '../textblockeditor/TextblockVariantTabs';
import ParticipantGroupDot from '../participant-group/ParticipantGroupDot';
import ParticipantGroupColorPicker from '../participant-group/admin/ParticipantGroupColorPicker';
import AddOnChips from './AddOnChips';
import { usePopup } from '../context/PopupContext';
import { useSuccessBar } from '../context/SuccessBarContext';
import { useOffline } from '../context/OfflineContext';
import useLocations from '../../hooks/useLocations';
import useParticipantGroups from '../../hooks/useParticipantGroups';
import {
  ADD_ON_COLOR_KEYS,
  ADD_ON_DESCRIPTION_MAX,
  ADD_ON_MAIL_HINT_MAX,
  ADD_ON_MAX_PER_PERSON_MAX,
  ADD_ON_MAX_QUANTITY_MAX,
  ADD_ON_NAME_MAX,
  ADD_ON_PLACEHOLDER_KEY_MAX,
  ADD_ON_PRICE_MAX,
  ADD_ON_STATIC_PLACEHOLDERS,
  addOnApiErrorMessage,
  addOnColorKey,
  addOnPlaceholderUsage,
  isAddOnUsed,
  locationsWithoutAddOnSetting,
  placeholderTokenEntries,
  validatePlaceholderKey,
} from '../../util/addOns';
import { parsePriceInput, toPriceInput } from '../../util/eventPrices';
import { copyText } from '../../util/clipboard';

export const ADD_ON_EDITOR_POPUP = 'addoneditor';

// Key of the "Standard" tab — 'default' so TextblockVariantTabs shows no location icon on it.
const STANDARD_TAB = 'default';
const PRICE_MAX_LABEL = ADD_ON_PRICE_MAX.toLocaleString('de-DE');
const QUANTITY_MAX_LABEL = ADD_ON_MAX_QUANTITY_MAX.toLocaleString('de-DE');

// Values of one tab (standard or location) as input strings; `omitted` = "entfällt" (price null).
function tabFromValues(src) {
  return {
    price: toPriceInput(src?.price),
    omitted: src?.price == null,
    maxQuantity: String(src?.maxQuantity ?? 0),
    bookableOnHomepage: src?.bookableOnHomepage === true,
    bookableOnSite: src?.bookableOnSite === true,
  };
}

// Popup form of a catalog entry (null = new). Deviations: saved ones by location name, then new ones (isNew;
// locationId null until a location is chosen). groupPrices: { [groupId]: input string } ('' = Standard).
function formFromEntry(entry) {
  const groupPrices = {};
  (entry?.participantGroupPrices ?? []).forEach(p => { groupPrices[p.participantGroupId] = toPriceInput(p.price); });
  return {
    name: entry?.name ?? '',
    description: entry?.description ?? '',
    // New entries: "Ohne Farbe" (the plain pill).
    colorKey: addOnColorKey(entry?.colorKey),
    maxPerPerson: String(entry?.maxPerPerson ?? 1),
    waitlistWhenFull: entry?.waitlistWhenFull === true,
    placeholderKey: entry?.placeholderKey ?? '',
    mailHint: entry?.mailHint ?? '',
    // New entries: bookable in the event (former "Artikel"), not offered on the homepage.
    standard: entry
      ? tabFromValues(entry)
      : { price: '', omitted: false, maxQuantity: '0', bookableOnHomepage: false, bookableOnSite: true },
    deviations: (entry?.locationSettings ?? []).map(s => ({
      key: `loc-${s.locationId}`,
      locationId: s.locationId,
      locationName: s.locationName ?? '',
      ...tabFromValues(s),
      isNew: false,
    })),
    groupPrices,
  };
}

// Whole number in [min, max] → number; '' → null; anything else → NaN.
function parseCountInput(value) {
  const raw = String(value ?? '').trim();
  if (raw === '') return null;
  return /^\d+$/.test(raw) ? Number(raw) : NaN;
}

function priceError(input) {
  const price = parsePriceInput(input);
  if (price == null) return 'Bitte einen Preis angeben.';
  if (Number.isNaN(price) || price > ADD_ON_PRICE_MAX) return `Der Preis muss zwischen 0 und ${PRICE_MAX_LABEL} € liegen.`;
  return null;
}

function maxQuantityError(input) {
  const n = parseCountInput(input);
  if (n == null) return 'Bitte eine Höchstmenge angeben (0 = unbegrenzt).';
  if (Number.isNaN(n) || n > ADD_ON_MAX_QUANTITY_MAX) {
    return `Die Höchstmenge muss eine ganze Zahl zwischen 0 und ${QUANTITY_MAX_LABEL} sein.`;
  }
  return null;
}

function tabErrors(tab) {
  if (tab.omitted) return null;
  const price = priceError(tab.price);
  const maxQuantity = maxQuantityError(tab.maxQuantity);
  return price || maxQuantity ? { price, maxQuantity } : null;
}

// Client checks mirroring AddOnValidator (the server validates again).
// → { name?, description?, maxPerPerson?, placeholderKey?, mailHint?, groupPrices: { [id]: msg }, tabs: { [key]: {…} } }
function validateForm(form, { duplicateName, otherKeys, keyLocked }) {
  const errors = { groupPrices: {}, tabs: {} };
  const name = form.name.trim();
  if (!name) errors.name = 'Bitte einen Namen angeben.';
  else if (name.length > ADD_ON_NAME_MAX) errors.name = `Der Name darf höchstens ${ADD_ON_NAME_MAX} Zeichen lang sein.`;
  else if (duplicateName) errors.name = 'Es gibt bereits einen Zusatz mit diesem Namen.';

  if (form.description.trim().length > ADD_ON_DESCRIPTION_MAX) {
    errors.description = `Die Beschreibung darf höchstens ${ADD_ON_DESCRIPTION_MAX} Zeichen lang sein.`;
  }

  const perPerson = parseCountInput(form.maxPerPerson);
  if (perPerson == null || Number.isNaN(perPerson) || perPerson < 1 || perPerson > ADD_ON_MAX_PER_PERSON_MAX) {
    errors.maxPerPerson = `Die Höchstmenge je Person muss eine ganze Zahl zwischen 1 und ${ADD_ON_MAX_PER_PERSON_MAX} sein.`;
  }

  if (!keyLocked) {
    const keyError = validatePlaceholderKey(form.placeholderKey, otherKeys);
    if (keyError) errors.placeholderKey = keyError;
  }
  if (form.mailHint.trim().length > ADD_ON_MAIL_HINT_MAX) {
    errors.mailHint = `Der Hinweistext darf höchstens ${ADD_ON_MAIL_HINT_MAX} Zeichen lang sein.`;
  }

  Object.entries(form.groupPrices).forEach(([id, value]) => {
    if (String(value ?? '').trim() === '') return;
    const price = parsePriceInput(value);
    if (Number.isNaN(price) || price > ADD_ON_PRICE_MAX) {
      errors.groupPrices[id] = `Der Preis muss zwischen 0 und ${PRICE_MAX_LABEL} € liegen.`;
    }
  });

  const standard = tabErrors(form.standard);
  if (standard) errors.tabs[STANDARD_TAB] = standard;
  form.deviations.forEach(d => {
    if (d.locationId == null) {
      errors.tabs[d.key] = { location: 'Bitte eine Location wählen.' };
      return;
    }
    const tab = tabErrors(d);
    if (tab) errors.tabs[d.key] = tab;
  });
  return errors;
}

const hasErrors = (errors) =>
  ['name', 'description', 'maxPerPerson', 'placeholderKey', 'mailHint'].some(k => !!errors[k])
  || Object.keys(errors.groupPrices).length > 0
  || Object.keys(errors.tabs).length > 0;

// Request values of a tab: an omitted tab keeps its other values when they are valid (they are ignored).
function tabBody(tab) {
  const maxQuantity = parseCountInput(tab.maxQuantity);
  return {
    price: tab.omitted ? null : parsePriceInput(tab.price),
    maxQuantity: Number.isInteger(maxQuantity) ? maxQuantity : 0,
    bookableOnHomepage: tab.bookableOnHomepage,
    bookableOnSite: tab.bookableOnSite,
  };
}

const usageText = (addOn) => {
  const templates = Number(addOn?.templateCount ?? 0);
  const processes = Number(addOn?.processCount ?? 0);
  const parts = [];
  if (templates > 0) parts.push(templates === 1 ? '1 Prozessvorlage' : `${templates} Prozessvorlagen`);
  if (processes > 0) parts.push(processes === 1 ? '1 Prozess' : `${processes} Prozesse`);
  return parts.join(' · ');
};

/**
 * Create/edit popup of a catalog add-on (/prices, admin): name, description, price & availability per location
 * (tabs "Standard · <Ort> · + Abweichung" like the flat-rate catalog), fixed prices per participant group, the
 * sign-up rules (max per person, "Ausgebucht → Warteliste") and the mail placeholder key with a live preview of
 * its placeholders. The key is locked while textblocks use its placeholders (the API answers 409 otherwise).
 * Open with openPopup(ADD_ON_EDITOR_POPUP, id | 'new') after setting `editing` (null = new) in the parent — the
 * parent keeps it after closing so the title survives the exit animation.
 * addOns: the loaded catalog (duplicate name / key checks). onSaved(addOnResponse), onRemoved(id) (404 on save).
 * Locations load only on the first "+ Abweichung"; groups come from the shared group list.
 */
function AddOnEditorPopup({ editing, addOns, onSaved, onRemoved }) {
  const {
    locations,
    loaded: locationsLoaded,
    failed: locationsFailed,
    ensureLoaded: ensureLocations,
  } = useLocations();
  const { groups, loaded: groupsLoaded, failed: groupsFailed } = useParticipantGroups();
  const { isOffline } = useOffline();
  const { isPopupOpen, popupData, closePopup } = usePopup();
  const { startSuccessBarLoading, startSuccessBarSuccess, closeSuccessBar } = useSuccessBar();

  const [form, setForm] = useState(() => formFromEntry(null));
  const [activeTab, setActiveTab] = useState(STANDARD_TAB);
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const editorOpen = isPopupOpen && popupData.name === ADD_ON_EDITOR_POPUP;
  const editorOpenRef = useRef(editorOpen);
  editorOpenRef.current = editorOpen;
  const editingRef = useRef(editing);
  editingRef.current = editing;
  const formRef = useRef(null);
  const saveRequestRef = useRef(0);
  const editorSessionRef = useRef(0); // +1 per opening of the editor
  const newDeviationSeqRef = useRef(0); // keys of new deviation tabs

  // Fresh form on every open, prefilled from the edited row.
  useEffect(() => {
    if (!editorOpen) return;
    editorSessionRef.current += 1;
    setForm(formFromEntry(editingRef.current));
    setActiveTab(STANDARD_TAB);
    setSubmitted(false);
    setError('');
  }, [editorOpen]);

  // Locations only for "+ Abweichung": requested on the click, and again once a connection is back when the click
  // happened offline (ensureLoaded is a no-op once requested or loaded).
  const hasPendingDeviation = form.deviations.some(d => d.locationId == null);
  useEffect(() => {
    if (editorOpen && hasPendingDeviation && !isOffline) ensureLocations();
  }, [editorOpen, hasPendingDeviation, isOffline, ensureLocations]);

  // The locations arrived after "+ Abweichung" was clicked: preselect the first free one for the new tab.
  useEffect(() => {
    if (!locationsLoaded) return;
    setForm(prev => {
      const pending = prev.deviations.find(d => d.locationId == null);
      const first = pending ? locationsWithoutAddOnSetting(prev.deviations, locations)[0] : null;
      if (!first) return prev;
      return {
        ...prev,
        deviations: prev.deviations.map(d =>
          (d.key === pending.key ? { ...d, locationId: first.id, locationName: first.name ?? '' } : d)),
      };
    });
  }, [locationsLoaded, locations]);

  const list = Array.isArray(addOns) ? addOns : [];
  const others = list.filter(a => a.id !== editing?.id);
  const trimmedName = form.name.trim();
  const duplicateName = !!trimmedName && others.some(a =>
    (a.name ?? '').trim().toLocaleLowerCase('de') === trimmedName.toLocaleLowerCase('de'));
  const otherKeys = others.map(a => a.placeholderKey).filter(Boolean);
  const placeholderUsage = addOnPlaceholderUsage(editing);
  // The server refuses changing/clearing a key whose placeholders appear in textblocks.
  const keyLocked = !!editing?.placeholderKey && placeholderUsage.length > 0;
  const errors = validateForm(form, { duplicateName, otherKeys, keyLocked });
  // Duplicate name and key problems show while typing, everything else after the first save attempt.
  const shownErrors = submitted
    ? errors
    : {
      groupPrices: {},
      tabs: {},
      ...(duplicateName ? { name: errors.name } : {}),
      ...(errors.placeholderKey ? { placeholderKey: errors.placeholderKey } : {}),
    };

  const setField = (key, value) => {
    setForm(prev => ({ ...prev, [key]: value }));
    setError('');
  };

  const setStandard = (patch) => {
    setForm(prev => ({ ...prev, standard: { ...prev.standard, ...patch } }));
    setError('');
  };

  const setDeviation = (key, patch) => {
    setForm(prev => ({ ...prev, deviations: prev.deviations.map(d => (d.key === key ? { ...d, ...patch } : d)) }));
    setError('');
  };

  const setGroupPrice = (groupId, value) => {
    setForm(prev => ({ ...prev, groupPrices: { ...prev.groupPrices, [groupId]: value } }));
    setError('');
  };

  // ── Tabs: Standard · <Ort> · … · + Abweichung ───────────────────────────────────────────────────────
  const activeDeviation = form.deviations.find(d => d.key === activeTab) ?? null;
  const shownTab = activeDeviation ? activeTab : STANDARD_TAB;
  const tabs = [
    { key: STANDARD_TAB, label: 'Standard', hasError: !!shownErrors.tabs[STANDARD_TAB] },
    ...form.deviations.map(d => ({
      key: d.key,
      label: d.locationName || 'Neue Abweichung',
      isNew: d.isNew,
      hasError: !!shownErrors.tabs[d.key],
    })),
  ];

  // Once the locations are known: no location left for another deviation (every one has one, or none exist) —
  // "+ Abweichung" is disabled then and says why (before the first click the locations are not loaded yet).
  const noFreeLocation = locationsLoaded && locationsWithoutAddOnSetting(form.deviations, locations).length === 0;
  const noFreeLocationText = locations.length === 0
    ? 'Es sind noch keine Locations angelegt – das geht auf der Seite „Locations“ (Startseite → Verwaltung).'
    : 'Für jede Location gibt es schon eine Abweichung.';

  // Adds a new deviation tab (values prefilled from the standard), or opens the one still without location.
  const handleAddDeviation = () => {
    if (!isOffline) ensureLocations();
    const pending = form.deviations.find(d => d.locationId == null);
    if (pending) {
      setActiveTab(pending.key);
      return;
    }
    if (noFreeLocation) return;
    const first = locationsWithoutAddOnSetting(form.deviations, locations)[0] ?? null;
    newDeviationSeqRef.current += 1;
    const key = `new-${newDeviationSeqRef.current}`;
    setForm(prev => ({
      ...prev,
      deviations: [...prev.deviations, {
        key,
        locationId: first?.id ?? null,
        locationName: first?.name ?? '',
        ...prev.standard,
        price: prev.standard.omitted ? '' : prev.standard.price,
        omitted: false,
        isNew: true,
      }],
    }));
    setActiveTab(key);
    setError('');
  };

  // Removed from the form only — deleted with "Speichern".
  const removeDeviation = (key) => {
    setForm(prev => ({ ...prev, deviations: prev.deviations.filter(d => d.key !== key) }));
    setActiveTab(STANDARD_TAB);
    setError('');
  };

  const handleLocationChange = (key, value) => {
    const location = locations.find(l => String(l.id) === String(value)) ?? null;
    setDeviation(key, { locationId: location?.id ?? null, locationName: location?.name ?? '' });
  };

  // ── Groups: active ones (the standard covers guests) plus every group that has or had a price ──────
  const savedGroupIds = new Set((editing?.participantGroupPrices ?? []).map(p => Number(p.participantGroupId)));
  const priceGroups = groups.filter(g =>
    (!g.isArchived && !g.isGuest)
    || savedGroupIds.has(g.id)
    || String(form.groupPrices[g.id] ?? '').trim() !== '');

  // ── Save ───────────────────────────────────────────────────────────────────────────────────────
  const focusFirstInvalid = () => {
    window.requestAnimationFrame(() => {
      const field = formRef.current?.querySelector('[aria-invalid="true"]');
      if (field) {
        field.scrollIntoView({ block: 'center', behavior: 'smooth' });
        field.focus({ preventScroll: true });
      }
    });
  };

  const handleSave = () => {
    if (saving || isOffline) return;
    setSubmitted(true);
    if (hasErrors(errors)) {
      // Show the first tab with an error unless the open one has one.
      if (!errors.tabs[shownTab]) {
        const firstInvalid = tabs.find(t => errors.tabs[t.key]);
        if (firstInvalid) setActiveTab(firstInvalid.key);
      }
      focusFirstInvalid();
      return;
    }
    const target = editing;
    const description = form.description.trim();
    const mailHint = form.mailHint.trim();
    const placeholderKey = keyLocked ? target.placeholderKey : form.placeholderKey.trim();
    const body = {
      name: trimmedName,
      description: description || null,
      colorKey: addOnColorKey(form.colorKey),
      maxPerPerson: parseCountInput(form.maxPerPerson),
      waitlistWhenFull: form.waitlistWhenFull,
      placeholderKey: placeholderKey || null,
      mailHint: mailHint || null,
      ...tabBody(form.standard),
      locationSettings: form.deviations.map(d => ({ locationId: d.locationId, ...tabBody(d) })),
      participantGroupPrices: Object.entries(form.groupPrices)
        .filter(([, value]) => String(value ?? '').trim() !== '')
        .map(([id, value]) => ({ participantGroupId: Number(id), price: parsePriceInput(value) })),
    };
    // The editor may be cancelled and reopened (for another row) while saving: only touch it if it still shows
    // this request's target.
    const request = ++saveRequestRef.current;
    const session = editorSessionRef.current;
    const stillEditing = () => editorOpenRef.current && editorSessionRef.current === session;
    setSaving(true);
    setError('');
    startSuccessBarLoading('Zusatz wird gespeichert');
    (target ? api.put(`/AddOn/Update/${target.id}`, body) : api.post('/AddOn/Create', body))
      .then(res => {
        onSaved?.(res.data);
        startSuccessBarSuccess('Zusatz wurde gespeichert');
        if (stillEditing()) closePopup();
      })
      .catch(err => {
        closeSuccessBar();
        // Deleted meanwhile (e.g. by another admin): drop the row.
        if (target && err?.response?.status === 404) onRemoved?.(target.id);
        if (!stillEditing()) return;
        setError(target && err?.response?.status === 404
          ? 'Der Zusatz existiert nicht mehr.'
          : addOnApiErrorMessage(err, 'Der Zusatz konnte nicht gespeichert werden.'));
      })
      .finally(() => { if (saveRequestRef.current === request) setSaving(false); });
  };

  const handleCopy = (token) => {
    copyText(token).then(ok => { if (ok) startSuccessBarSuccess('In Zwischenablage kopiert'); });
  };

  // ── Render helpers ─────────────────────────────────────────────────────────────────────────────
  const fieldError = (message) => (message ? <p className="addOnEditorFieldError">{message}</p> : null);

  // Location of a new deviation tab: dropdown of the free locations, or why there is none (inline).
  const renderLocationPicker = (d) => {
    let picker;
    if (!locationsLoaded) {
      if (isOffline) {
        picker = <p className="formFieldHint">Locations sind offline nicht verfügbar.</p>;
      } else if (locationsFailed) {
        picker = (
          <div className="addOnEditorInlineState">
            <p className="formFieldHint">Locations konnten nicht geladen werden.</p>
            <ActionButton icon="refresh" label="Erneut laden" onClick={() => ensureLocations()} />
          </div>
        );
      } else {
        picker = <p className="formFieldHint">Locations werden geladen …</p>;
      }
    } else if (locations.length === 0) {
      picker = (
        <p className="formFieldHint">
          Es sind noch keine Locations angelegt – das geht auf der Seite „Locations“ (Startseite → Verwaltung).
        </p>
      );
    } else {
      const free = locationsWithoutAddOnSetting(form.deviations.filter(x => x.key !== d.key), locations);
      picker = free.length === 0
        ? <p className="formFieldHint">Für jede Location gibt es schon eine Abweichung.</p>
        : (
          <CustomDropdown
            icon="location_on"
            ariaLabel="Location"
            value={d.locationId == null ? '' : String(d.locationId)}
            onChange={e => handleLocationChange(d.key, e.target.value)}
          >
            <option value="" disabled>– Location wählen –</option>
            {free.map(l => <option key={l.id} value={String(l.id)}>{l.name}</option>)}
          </CustomDropdown>
        );
    }
    return (
      <div className="addOnEditorLocationPicker">
        <span className="addOnEditorMiniTitle">Location</span>
        {picker}
        {fieldError(shownErrors.tabs[d.key]?.location)}
      </div>
    );
  };

  // Price + "entfällt", max per event and the two booking switches of the open tab (Standard or a location).
  // "Entfällt" disables the other fields of the tab.
  const renderTabFields = ({ key, tab, omittedLabel, onChange }) => {
    const tabError = shownErrors.tabs[key] ?? {};
    return (
      <React.Fragment>
        <div className="formFieldWrapper">
          <label className="formFieldLabel" htmlFor={`addOnPrice-${key}`}>Preis (€)</label>
          <input
            id={`addOnPrice-${key}`}
            type="number"
            min={0}
            max={ADD_ON_PRICE_MAX}
            step={0.01}
            inputMode="decimal"
            placeholder={tab.omitted ? 'entfällt' : '0,00'}
            value={tab.price}
            disabled={tab.omitted}
            onChange={e => onChange({ price: e.target.value })}
            aria-invalid={tabError.price ? 'true' : undefined}
            className={tabError.price ? 'isInvalid' : ''}
          />
        </div>
        {fieldError(tabError.price)}
        <CustomTogglebox
          placeholder={omittedLabel}
          checked={tab.omitted}
          onChange={e => onChange({ omitted: e.target.checked })}
        />
        <fieldset className="addOnEditorTabFields" disabled={tab.omitted}>
          <div className="formFieldWrapper">
            <label className="formFieldLabel" htmlFor={`addOnMaxQuantity-${key}`}>Höchstmenge je Spieltag</label>
            <input
              id={`addOnMaxQuantity-${key}`}
              type="number"
              min={0}
              max={ADD_ON_MAX_QUANTITY_MAX}
              step={1}
              inputMode="numeric"
              placeholder="0 = unbegrenzt"
              value={tab.maxQuantity}
              onChange={e => onChange({ maxQuantity: e.target.value })}
              aria-invalid={tabError.maxQuantity ? 'true' : undefined}
              className={tabError.maxQuantity ? 'isInvalid' : ''}
            />
          </div>
          {fieldError(tabError.maxQuantity)}
          <p className="formFieldHint">
            0 = unbegrenzt. Zählt Anmelde-Buchungen und Artikel-Verkäufe eines Spieltags zusammen.
          </p>
          <CustomTogglebox
            placeholder="Über die Homepage buchbar (Standard für neue Prozesse)"
            checked={tab.bookableOnHomepage}
            onChange={e => onChange({ bookableOnHomepage: e.target.checked })}
          />
          <CustomTogglebox
            placeholder="Im Event buchbar (Artikel)"
            checked={tab.bookableOnSite}
            onChange={e => onChange({ bookableOnSite: e.target.checked })}
          />
        </fieldset>
      </React.Fragment>
    );
  };

  const renderTabPanel = () => {
    if (!activeDeviation) {
      return (
        <React.Fragment key={STANDARD_TAB}>
          {renderTabFields({
            key: STANDARD_TAB,
            tab: form.standard,
            omittedLabel: 'Entfällt im Standard – nur an Locations mit eigener Abweichung',
            onChange: setStandard,
          })}
          {form.standard.omitted && form.deviations.length === 0 && (
            <p className="formFieldHint">
              Ohne Abweichung wird der Zusatz damit an keiner Location angeboten – er steht nur in der Preisliste.
            </p>
          )}
        </React.Fragment>
      );
    }
    const d = activeDeviation;
    return (
      <React.Fragment key={d.key}>
        {d.isNew && renderLocationPicker(d)}
        {d.locationId != null && renderTabFields({
          key: d.key,
          tab: d,
          omittedLabel: 'Entfällt an dieser Location',
          onChange: patch => setDeviation(d.key, patch),
        })}
        <div className="addOnEditorPanelActions">
          <ActionButton icon="delete" label="Abweichung entfernen" type={1} onClick={() => removeDeviation(d.key)} />
        </div>
      </React.Fragment>
    );
  };

  const renderGroupPrices = () => {
    if (!groupsLoaded) {
      return (
        <p className="formFieldHint">
          {groupsFailed ? 'Gruppen konnten nicht geladen werden.' : 'Gruppen werden geladen …'}
        </p>
      );
    }
    if (priceGroups.length === 0) {
      return <p className="formFieldHint">Es gibt keine Gruppen außer Gast – der Standardpreis gilt für alle.</p>;
    }
    return (
      <div className="addOnEditorGroupList" role="group" aria-label="Preise je Gruppe">
        {priceGroups.map(g => {
          const message = shownErrors.groupPrices[g.id];
          const inputId = `addOnGroupPrice-${g.id}`;
          return (
            <div key={g.id} className="addOnEditorGroupRow">
              <span className="addOnEditorGroupName">
                <ParticipantGroupDot participantGroup={g} withTooltip={false} />
                <label className="addOnEditorGroupNameText" htmlFor={inputId}>{g.name}</label>
                {g.isArchived && <span className="addOnEditorGroupChip">archiviert</span>}
              </span>
              <input
                id={inputId}
                type="number"
                min={0}
                max={ADD_ON_PRICE_MAX}
                step={0.01}
                inputMode="decimal"
                placeholder="Standard"
                value={form.groupPrices[g.id] ?? ''}
                onChange={e => setGroupPrice(g.id, e.target.value)}
                aria-invalid={message ? 'true' : undefined}
                className={message ? 'isInvalid' : ''}
              />
              {message && <p className="addOnEditorFieldError addOnEditorGroupError">{message}</p>}
            </div>
          );
        })}
      </div>
    );
  };

  const effectiveKey = keyLocked ? editing.placeholderKey : form.placeholderKey.trim();
  const tokenEntries = shownErrors.placeholderKey ? [] : placeholderTokenEntries(effectiveKey, trimmedName);
  const perPersonCount = parseCountInput(form.maxPerPerson);
  const signupControl = perPersonCount === 1
    ? 'Im Anmeldeformular ein Häkchen.'
    : Number.isInteger(perPersonCount) && perPersonCount > 1
      ? `Im Anmeldeformular ein Mengenfeld 0–${perPersonCount}.`
      : '';
  const used = !!editing && isAddOnUsed(editing);

  return (
    <Popup
      name={ADD_ON_EDITOR_POPUP}
      headerText={editing ? 'Zusatz bearbeiten' : 'Neuer Zusatz'}
      confirmText={saving ? 'Speichert …' : 'Speichern'}
      type="info"
      onConfirm={handleSave}
      dontCloseOnConfirm
      withCancel
      wide
      confirmDisabled={saving || isOffline}
    >
      <div className="addOnEditor" ref={formRef}>
        <section className="addOnEditorSection">
          <div className="formFieldWrapper">
            <label className="formFieldLabel" htmlFor="addOnName">Name</label>
            <input
              id="addOnName"
              type="text"
              maxLength={ADD_ON_NAME_MAX}
              placeholder="z. B. Leihausrüstung"
              autoComplete="off"
              value={form.name}
              onChange={e => setField('name', e.target.value)}
              aria-invalid={shownErrors.name ? 'true' : undefined}
              className={shownErrors.name ? 'isInvalid' : ''}
            />
          </div>
          {fieldError(shownErrors.name)}
          <div className="formFieldWrapper">
            <label className="formFieldLabel" htmlFor="addOnDescription">Beschreibung</label>
            <textarea
              id="addOnDescription"
              className={`addOnEditorTextarea${shownErrors.description ? ' isInvalid' : ''}`}
              rows={2}
              maxLength={ADD_ON_DESCRIPTION_MAX}
              placeholder="z. B. Waffe, Maske und Magazine (optional)"
              value={form.description}
              onChange={e => setField('description', e.target.value)}
              aria-invalid={shownErrors.description ? 'true' : undefined}
            />
          </div>
          {fieldError(shownErrors.description)}
          <p className="formFieldHint">Erscheint in der Preisliste.</p>
        </section>

        <section className="addOnEditorSection">
          <h3>Farbe</h3>
          <ParticipantGroupColorPicker
            value={form.colorKey}
            onChange={key => setField('colorKey', key)}
            keys={ADD_ON_COLOR_KEYS}
            ariaLabel="Farbe des Zusatzes"
          />
          <div className="addOnEditorColorPreview">
            <span className="addOnEditorMiniTitle">Vorschau</span>
            <AddOnChips
              items={[{ key: 'preview', label: trimmedName || 'Zusatz', colorKey: form.colorKey }]}
              ariaLabel="Vorschau"
            />
          </div>
          <p className="formFieldHint">
            Färbt die Pillen des Zusatzes in Teilnehmerliste, Inbox und Übersichten sowie sein Symbol in der Preisliste und
            seine Kacheln und Balken in der Statistik. Ausgebucht bleibt die Pille gelb.
          </p>
        </section>

        <section className="addOnEditorSection">
          <h3>Preis &amp; Verfügbarkeit</h3>
          <TextblockVariantTabs
            tabs={tabs}
            activeKey={shownTab}
            onSelect={setActiveTab}
            onAdd={handleAddDeviation}
            ariaLabel="Preis und Verfügbarkeit je Location"
            addLabel="Abweichung"
            addDisabled={saving || noFreeLocation}
            addTitle={noFreeLocation ? noFreeLocationText : undefined}
            idPrefix="addOnSettings"
          />
          <p className="formFieldHint">
            Neue Prozesse an einer Location übernehmen deren Preis, Höchstmenge und Homepage-Einstellung, sonst den
            Standard. „Im Event buchbar“ und der Preis im Event gelten sofort für die Location des Spieltags.
          </p>
          {/* A tab still without location explains it in its own panel. */}
          {noFreeLocation && !hasPendingDeviation && <p className="formFieldHint">{noFreeLocationText}</p>}
          <div
            className="addOnEditorPanel"
            id="addOnSettingsPanel"
            role="tabpanel"
            aria-labelledby={`addOnSettingsTab-${shownTab}`}
          >
            {renderTabPanel()}
          </div>
        </section>

        <section className="addOnEditorSection">
          <h3>Preise je Gruppe</h3>
          <p className="formFieldHint">
            Optional: fester Preis einer Gruppe an allen Locations – bei der Anmeldung und im Event. Leer = Standard-
            bzw. Location-Preis, 0 = kostenlos.
          </p>
          {renderGroupPrices()}
        </section>

        <section className="addOnEditorSection">
          <h3>Bei der Anmeldung</h3>
          <div className="formFieldWrapper">
            <label className="formFieldLabel" htmlFor="addOnMaxPerPerson">Höchstmenge je Person</label>
            <input
              id="addOnMaxPerPerson"
              type="number"
              min={1}
              max={ADD_ON_MAX_PER_PERSON_MAX}
              step={1}
              inputMode="numeric"
              value={form.maxPerPerson}
              onChange={e => setField('maxPerPerson', e.target.value)}
              aria-invalid={shownErrors.maxPerPerson ? 'true' : undefined}
              className={shownErrors.maxPerPerson ? 'isInvalid' : ''}
            />
          </div>
          {fieldError(shownErrors.maxPerPerson)}
          <p className="formFieldHint">
            {signupControl} Gilt nur bei der Anmeldung – im Event (Artikel) gibt es keine Grenze je Person.
          </p>
          <CustomTogglebox
            placeholder="Ausgebucht → Warteliste"
            checked={form.waitlistWhenFull}
            onChange={e => setField('waitlistWhenFull', e.target.checked)}
          />
          <p className="formFieldHint">
            {form.waitlistWhenFull
              ? 'Ist der Zusatz ausgebucht, kommt die ganze Anmeldung auf die Zusatz-Warteliste (Mail „Zusatz-Warteliste“).'
              : 'Ist der Zusatz ausgebucht, wird die Person ohne ihn bzw. mit weniger davon angenommen – der Posteingang zeigt das an.'}
          </p>
        </section>

        <section className="addOnEditorSection">
          <h3>Mail-Platzhalter</h3>
          <div className="formFieldWrapper">
            <label className="formFieldLabel" htmlFor="addOnPlaceholderKey">Kürzel</label>
            <input
              id="addOnPlaceholderKey"
              type="text"
              maxLength={ADD_ON_PLACEHOLDER_KEY_MAX}
              placeholder="z. B. Leihe (optional)"
              autoComplete="off"
              autoCapitalize="none"
              spellCheck={false}
              value={keyLocked ? editing.placeholderKey : form.placeholderKey}
              disabled={keyLocked}
              onChange={e => setField('placeholderKey', e.target.value)}
              aria-invalid={shownErrors.placeholderKey ? 'true' : undefined}
              aria-describedby={editing?.placeholderKey ? 'addOnPlaceholderUsage' : undefined}
              className={`addOnEditorKeyInput${shownErrors.placeholderKey ? ' isInvalid' : ''}`}
            />
          </div>
          {fieldError(shownErrors.placeholderKey)}
          {editing?.placeholderKey && (
            <p className={`addOnEditorUsage${keyLocked ? ' isLocked' : ''}`} id="addOnPlaceholderUsage">
              <span className="material-symbols-rounded" aria-hidden="true">{keyLocked ? 'lock' : 'mail'}</span>
              <span>
                {keyLocked
                  ? <>Verwendet in: <b>{placeholderUsage.join(', ')}</b>. Ändern oder entfernen geht erst, wenn kein
                    Textbaustein die Platzhalter mehr enthält.</>
                  : 'Noch in keinem Textbaustein verwendet.'}
              </span>
            </p>
          )}
          <div className="formFieldWrapper">
            <label className="formFieldLabel" htmlFor="addOnMailHint">Hinweistext</label>
            <textarea
              id="addOnMailHint"
              className={`addOnEditorTextarea${shownErrors.mailHint ? ' isInvalid' : ''}`}
              rows={3}
              maxLength={ADD_ON_MAIL_HINT_MAX}
              placeholder="z. B. Eine Leihwaffe ist für dich reserviert."
              value={form.mailHint}
              onChange={e => setField('mailHint', e.target.value)}
              aria-invalid={shownErrors.mailHint ? 'true' : undefined}
            />
          </div>
          {fieldError(shownErrors.mailHint)}
          <p className="formFieldHint">
            HTML ist erlaubt; reiner Text wird als Absatz eingefügt. Ohne Kürzel wird der Hinweistext nicht verwendet.
          </p>

          <div className="addOnEditorTokens" aria-live="polite">
            <span className="addOnEditorMiniTitle">Vorschau der Platzhalter</span>
            {tokenEntries.length === 0 ? (
              <p className="formFieldHint">
                {effectiveKey && shownErrors.placeholderKey
                  ? 'Sobald das Kürzel gültig ist, erscheinen hier seine Platzhalter.'
                  : 'Ohne Kürzel bekommt der Zusatz keine eigenen Platzhalter.'}
              </p>
            ) : (
              <ul className="addOnEditorTokenList">
                {tokenEntries.map(entry => (
                  <li key={entry.key} className="addOnEditorToken">
                    <button
                      type="button"
                      className="addOnEditorTokenKey"
                      onClick={() => handleCopy(entry.key)}
                      title="In Zwischenablage kopieren"
                    >
                      {entry.key}
                    </button>
                    <span className="addOnEditorTokenLabel">{entry.label}</span>
                  </li>
                ))}
              </ul>
            )}
            <p className="formFieldHint">
              Außerdem gibt es für alle Zusätze {ADD_ON_STATIC_PLACEHOLDERS[0].key} (gebuchte Zusätze des Empfängers)
              und {ADD_ON_STATIC_PLACEHOLDERS[1].key} (ausgebuchte Zusätze, nur in der Mail „Zusatz-Warteliste“).
            </p>
          </div>
        </section>

        {used && (
          <div className="addOnEditorNotice">
            <span className="material-symbols-rounded" aria-hidden="true">info</span>
            <div className="addOnEditorNoticeText">
              <p>Wird schon verwendet ({usageText(editing)}).</p>
              <p>
                <b>Gilt sofort überall:</b> Name, Beschreibung, Höchstmenge je Person, „Ausgebucht → Warteliste“,
                Mail-Platzhalter und „Im Event buchbar“ – Artikel im Event kosten immer den aktuellen Katalogpreis.
              </p>
              <p>
                <b>Nur für neue Prozesse:</b> Preis und Gruppenpreise bei der Anmeldung, Höchstmenge je Spieltag und
                „Über die Homepage buchbar“. Bestehende Prozesse behalten ihre Werte – anpassbar auf der Prozessseite.
              </p>
            </div>
          </div>
        )}
        {isOffline && (
          <div className="formOfflineHint">
            <span className="material-symbols-rounded formOfflineHintIcon" aria-hidden="true">cloud_off</span>
            Offline – Speichern nicht verfügbar
          </div>
        )}
        {error && <div className="newError" role="alert">{error}</div>}
      </div>
    </Popup>
  );
}

export default AddOnEditorPopup;
